import React, { memo, useCallback } from 'react';

import { Box, Typography } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { AiProcessExecutionStatus } from 'constants/aiProcessRuns';

import { AiActivityTestIds } from '../../../AiActivity.testIds';
import { formatDuration, getStepReasonLabel } from '../../../AiActivity.utils';
import StatusIcon from '../../../RunRow/components/StatusIcon';
import { StatusIconAppearance } from '../../../RunRow/RunRow.constants';
import StatusPill from '../../../StatusPill';

import {
  durationStyles,
  getConnectorStyles,
  getRowCardStyles,
  getRowStyles,
  metaRowStyles,
  nodeColumnStyles,
  nodeStyles,
  runningPulseStyles,
  stepNameStyles,
} from './StepNavRow.styles';

const StepNavRow = ({ step, number, isSelected, hasConnector, blockedBy, onSelect }) => {
  const onClick = useCallback(() => onSelect(step.id), [onSelect, step.id]);

  const connectorAppearance =
    StatusIconAppearance[step.status] ?? StatusIconAppearance[AiProcessExecutionStatus.PENDING];

  const isRunning = step.status === AiProcessExecutionStatus.RUNNING;
  const isBlocked = !_.isNil(blockedBy);
  const reasonLabel = getStepReasonLabel(step);

  return (
    <Box
      data-testid={AiActivityTestIds.STEP_NAV_ROW}
      role="button"
      sx={getRowStyles(isBlocked)}
      onClick={onClick}
    >
      <Box sx={nodeColumnStyles}>
        <Box sx={nodeStyles}>
          <StatusIcon compact status={step.status} />
          {isRunning && <Box sx={runningPulseStyles} />}
        </Box>
        {hasConnector && <Box sx={getConnectorStyles(connectorAppearance)} />}
      </Box>
      <Box sx={getRowCardStyles(isSelected)}>
        <Typography sx={stepNameStyles} variant="medium14">
          {number}. {step.name}
        </Typography>
        <Box sx={metaRowStyles}>
          {isBlocked ? (
            <Typography data-testid={AiActivityTestIds.STEP_NAV_ROW_BLOCKED} variant="regular12">
              Waits on {blockedBy.name}
            </Typography>
          ) : (
            <>
              <StatusPill status={step.status} />
              {!_.isNil(reasonLabel) && (
                <Typography data-testid={AiActivityTestIds.STEP_NAV_ROW_REASON} variant="regular12">
                  {reasonLabel}
                </Typography>
              )}
              <Typography sx={durationStyles} variant="regular12">
                {formatDuration(step)}
              </Typography>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

StepNavRow.propTypes = {
  step: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    status: PropTypes.string,
    startedAt: PropTypes.string,
    completedAt: PropTypes.string,
    errorReason: PropTypes.string,
  }).isRequired,
  hasConnector: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool.isRequired,
  number: PropTypes.number.isRequired,
  onSelect: PropTypes.func.isRequired,
  blockedBy: PropTypes.shape({ name: PropTypes.string }),
};

export default memo(StepNavRow);
